import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { mediaApi } from "../api";
import MediaCard from "../components/MediaCard";

const TITLES = { AU: "Áudios", V2: "Vídeos", VI: "Vídeos", VD: "Vídeos" };

export default function MediaCategory() {
  const { type } = useParams();
  const [all, setAll] = useState([]);

  useEffect(() => {
    mediaApi.contentAll(type).then((data) => setAll(data || []));
  }, [type]);

  // Sections group leaves by their parent folder — mas apenas faz sentido
  // para áudios (AU), onde há colecções curadas (IHTP vs Scott Schmaren).
  // Para vídeos cada workshop é uma folder individual e agrupar por
  // workshop resulta em ~15 secções minúsculas — melhor mostrar grid
  // plano nesses casos.
  const sections = useMemo(() => {
    if (type !== "AU") {
      const leaves = all.filter((i) => i.MediaUrl);
      return leaves.length
        ? [{ key: "all", title: "", items: leaves }]
        : [];
    }
    const folders = all.filter(
      (i) =>
        i.ID_ConteudoMediaCenterPai === null &&
        i.ID_TipoConteudoMediaCenter === "AU"
    );
    const folderById = new Map(folders.map((f) => [f.ID_ConteudoMediaCenter, f]));
    const groups = new Map();
    for (const it of all) {
      if (!it.MediaUrl) continue;
      const parentId = it.ID_ConteudoMediaCenterPai;
      const key = folderById.has(parentId) ? parentId : "__outros__";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(it);
    }
    const ordered = folders
      .filter((f) => groups.has(f.ID_ConteudoMediaCenter))
      .sort((a, b) => (a.Ordem || 0) - (b.Ordem || 0))
      .map((f) => ({
        key: `folder-${f.ID_ConteudoMediaCenter}`,
        title: f.Descricao,
        items: groups.get(f.ID_ConteudoMediaCenter),
      }));
    if (groups.has("__outros__")) {
      ordered.push({
        key: "outros",
        title: "Outros",
        items: groups.get("__outros__"),
      });
    }
    return ordered;
  }, [all, type]);

  const showFolders = type === "AU" && sections.length > 1;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 md:px-8 lg:px-12 py-12 max-w-[1600px] mx-auto"
      data-testid={`media-category-${type}`}
    >
      <p className="text-xs tracking-[0.3em] uppercase text-[#D1CFC9] font-bold mb-2">Categoria</p>
      <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light mb-8 md:mb-10">
        {TITLES[type] || "Conteúdos"}
      </h1>

      {showFolders ? (
        sections.map((sec) => (
          <section key={sec.key} className="mb-14" data-testid={`media-category-section-${sec.key}`}>
            <div className="flex items-baseline gap-3 mb-5">
              <h2 className="font-serif text-2xl md:text-3xl">{sec.title}</h2>
              <span className="text-xs text-[#D1CFC9]/60 font-bold tracking-widest uppercase">
                {sec.items.length}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {sec.items.map((i) => (
                <MediaCard key={i.ID_ConteudoMediaCenter} item={i} fluid />
              ))}
            </div>
          </section>
        ))
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
          {sections.flatMap((s) => s.items).map((i) => (
            <MediaCard key={i.ID_ConteudoMediaCenter} item={i} fluid />
          ))}
        </div>
      )}
    </motion.div>
  );
}
